import { createDiscoveryProject } from './discoveryProjectService';
import type { Category } from './database.client.types';
import { supabase, toError } from './supabase';
import { trackFunnelEvent } from './funnelEvents';
import {
    createOnboardingRequestId,
    createOrResumeOnboardingProjectIntent,
    finalizeOnboardingProjectIntent,
    onboardingActivationDestination,
} from './onboardingProjectIntent';
import type { OnboardingJourneySource } from './onboardingProjectIntentContract';
import { withOnboardingSeedPaperId } from './onboardingProjectIntentRequest';
import { buildPaperSeedDraft } from './paperSeedDraft';

export interface ChatCategoryDraft {
    id: number;
    code: string;
    name: string;
    source_id: number;
}

export interface ChatDigestDraft {
    projectName?: string;
    categories: ChatCategoryDraft[];
    interestText: string;
    include: string[];
    exclude: string[];
}

interface CreateProjectFromChatOptions {
    source?: 'chat' | 'template' | 'forward' | 'direct' | 'unknown';
    chatSessionId?: string | null;
    seedPaperId?: string | null;
}

export interface ChatProjectCreationResult {
    projectId: string;
    destination: string;
    requiresVerification: boolean;
}

function onboardingJourneySource(
    source: NonNullable<CreateProjectFromChatOptions['source']>
): OnboardingJourneySource {
    if (source === 'forward') return 'forward';
    if (source === 'direct') return 'direct';
    return 'chat';
}

function addSetupSource(destination: string, source: OnboardingJourneySource): string {
    if (source !== 'forward') {
        return destination;
    }
    const [path, query = ''] = destination.split('?');
    const searchParams = new URLSearchParams(query);
    searchParams.set('setup_source', 'forwarded');
    return `${path}?${searchParams.toString()}`;
}

function activationResumeDestination(
    projectId: string,
    journeySource: OnboardingJourneySource,
    seedPaperId?: string | null
): string {
    return addSetupSource(
        withOnboardingSeedPaperId(
            onboardingActivationDestination(projectId),
            seedPaperId
        ),
        journeySource
    );
}

function normalizeCategoryDraft(
    category: Pick<Category, 'id' | 'code' | 'name' | 'source_id'>
): ChatCategoryDraft | null {
    if (category.source_id === null) {
        return null;
    }

    return {
        id: category.id,
        code: category.code,
        name: category.name || category.code,
        source_id: category.source_id,
    };
}

export async function createProjectFromChatDraft(
    draft: ChatDigestDraft,
    options: CreateProjectFromChatOptions = {}
): Promise<ChatProjectCreationResult> {
    const source = options.source || 'chat';
    const journeySource = onboardingJourneySource(source);
    trackFunnelEvent('project_create_started', {
        source,
        chat_session_id: options.chatSessionId || null,
    });

    let createdProject: { projectId: string; projectName: string };
    try {
        createdProject = await createDiscoveryProject({
            projectName: draft.projectName,
            interestText: draft.interestText,
            categories: draft.categories as unknown as Category[],
            includeKeywords: draft.include,
            excludeKeywords: draft.exclude,
        });
    } catch (error: any) {
        trackFunnelEvent('project_create_failed', {
            source,
            chat_session_id: options.chatSessionId || null,
            error_code: error?.code,
            error_message: error?.message || 'project_create_failed',
        });
        throw error;
    }

    const { projectId, projectName } = createdProject;
    try {
        const storedIntent = await createOrResumeOnboardingProjectIntent({
            projectId,
            clientRequestId: createOnboardingRequestId(),
            journeySource,
            chatSessionId: options.chatSessionId ?? null,
            generatedProjectName: projectName,
            seedPaperId: options.seedPaperId ?? null,
        });
        const requiresVerification = storedIntent.intent.verificationRequired;
        const destination = requiresVerification
            ? activationResumeDestination(projectId, journeySource, options.seedPaperId)
            : addSetupSource(
                (await finalizeOnboardingProjectIntent(
                    storedIntent.intent,
                    storedIntent.directFinalizeClientRequestId
                )).destination,
                journeySource
            );

        trackFunnelEvent('project_create_succeeded', {
            source,
            project_id: projectId,
            chat_session_id: options.chatSessionId || null,
            requires_verification: requiresVerification,
        });

        return { projectId, destination, requiresVerification };
    } catch (error: any) {
        const destination = activationResumeDestination(
            projectId,
            journeySource,
            options.seedPaperId
        );
        trackFunnelEvent('project_create_succeeded', {
            source,
            project_id: projectId,
            chat_session_id: options.chatSessionId || null,
            requires_verification: true,
            activation_resume_required: true,
        });
        console.warn(
            '⚠️ [ChatProjectCreation] Project created; activation will resume setup:',
            { projectId, errorCode: error?.code }
        );
        return { projectId, destination, requiresVerification: true };
    }
}

export async function loadTemplateDraft(templateProjectId: string): Promise<ChatDigestDraft> {
    const { data, error } = await supabase
        .from('project')
        .select(`
            interest_description,
            name,
            project_category_interests (
                category (
                    id,
                    code,
                    name,
                    source_id
                )
            ),
            project_keywords:project_keyword (
                keyword,
                is_exclusion
            )
        `)
        .eq('id', templateProjectId)
        .is('deleted_at', null)
        .single();

    if (error || !data) {
        throw toError(error || new Error('Template not found'));
    }

    const categories = (data.project_category_interests || [])
        .map((pci: any) => pci.category)
        .filter(Boolean)
        .map((category: Pick<Category, 'id' | 'code' | 'name' | 'source_id'>) => normalizeCategoryDraft(category))
        .filter((category): category is ChatCategoryDraft => category !== null);

    const keywords = data.project_keywords || [];
    const include = keywords
        .filter((keyword: any) => !keyword.is_exclusion)
        .map((keyword: any) => keyword.keyword);
    const exclude = keywords
        .filter((keyword: any) => keyword.is_exclusion)
        .map((keyword: any) => keyword.keyword);

    return {
        projectName: data.name || undefined,
        categories,
        interestText: data.interest_description || '',
        include,
        exclude,
    };
}

export async function loadPaperSeedDraft(seedPaperId: string): Promise<ChatDigestDraft> {
    const { data, error } = await supabase
        .from('paper')
        .select(`
            title,
            abstract,
            paper_categories (
                category:category_id (
                    id,
                    code,
                    name,
                    source_id
                )
            )
        `)
        .eq('id', seedPaperId)
        .single();

    if (error || !data) {
        throw toError(error || new Error('Paper not found'));
    }

    const categories = (data.paper_categories || [])
        .map((paperCategory: any) => paperCategory.category)
        .filter(Boolean)
        .map((category: Pick<Category, 'id' | 'code' | 'name' | 'source_id'>) => normalizeCategoryDraft(category))
        .filter((category): category is ChatCategoryDraft => category !== null);

    return buildPaperSeedDraft({
        title: data.title,
        abstract: data.abstract,
        categories,
    });
}
